
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ReactNode, useEffect, useState } from "react";

type ProtectedRouteProps = {
  requiredRoles?: Array<"admin" | "manager" | "user" | "super_admin">;
  children: ReactNode;
  allowUnauthenticated?: boolean; // New prop to optionally allow unauthenticated access
};

const ProtectedRoute = ({ requiredRoles, children, allowUnauthenticated = false }: ProtectedRouteProps) => {
  const { user, userRoles, isLoading } = useAuth();
  const location = useLocation();
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);

  useEffect(() => {
    // Skip if still loading
    if (isLoading) return;
    
    // If we allow unauthenticated access and there's no user, we're authorized
    if (allowUnauthenticated && !user) {
      setIsAuthorized(true);
      return;
    }
    
    // Not authenticated
    if (!user) {
      setIsAuthorized(false);
      return;
    }
    
    // No specific roles required
    if (!requiredRoles || requiredRoles.length === 0) {
      setIsAuthorized(true);
      return;
    }
    
    // Check if user has required role
    const hasRequiredRole = requiredRoles.some(role => userRoles.includes(role));
    setIsAuthorized(hasRequiredRole);
  }, [user, userRoles, isLoading, requiredRoles, allowUnauthenticated]);

  // Show loading state while determining authorization
  if (isLoading || isAuthorized === null) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verifying permissions...</p>
        </div>
      </div>
    );
  }

  // If we allow unauthenticated access and there's no user, render children
  if (allowUnauthenticated && !user) {
    return <>{children}</>;
  }

  // Redirect to login if not authenticated
  if (!user) {
    // Save the current path in the URL to redirect back after login
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }

  // Redirect to unauthorized page if not authorized
  if (!isAuthorized) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Render children if authorized
  return <>{children}</>;
};

export default ProtectedRoute;
